// composables/useInitApp.ts
import { storeToRefs } from "pinia";
import { useStoryblokApi } from "@storyblok/vue";

// Type imports
import type { MenuLink } from "~/types/storyblok/storyblok";

// Regular imports
import { useFetchCategoryTree } from "~/composables/useFetchCategoryTree";
import { useLoadUser } from "~/composables/useLoadUser";
import { getStoryblokVersion } from "~/utils/storyblok/getStoryblokVersion";

export const useInitApp = () => {
	const appStore = useAppStore();
	const cartStore = useCartStore();
	const productStore = useProductStore();
	const storyblokApi = useStoryblokApi();
	const {
		getApplication,
		getParametricsInformation,
		getPriceLists,
		getMenuCategoriesFromNorce
	} = useNorcePwa();
	const { fetchCategoryTree } = useFetchCategoryTree();
	const { getUser } = storeToRefs(userStore());
	const { getUserByEmailIfHasSession } = useLoadUser();
	const norceStore = useNorceStore();

	const filterMenuCategories = (categories: MenuLink[]) =>
		categories.filter((cat) => cat.component !== "categories_multi_list");

	const initApp = async (iso: string, locale: string) => {
		norceStore.setCultureCode(iso);
		const [{ data }, categoriesTree, priceLists] = await Promise.all([
			storyblokApi.get("cdn/stories/layout", {
				version: getStoryblokVersion(),
				language: iso,
				resolve_links: "url"
			}),
			fetchCategoryTree(locale),
			getPriceLists({ cultureCode: iso }),
			getUserByEmailIfHasSession()
		]);
		const application = await getApplication();

		setColors(data.story.content.colors);

		const norceMenuLeft =
			data.story.content.menu_categories_left.filter(
				(menuCat: MenuLink) => menuCat.component === "categories_multi_list"
			)[0]?.categories || [];
		const norceMenuRight =
			data.story.content.menu_categories_right.filter(
				(menuCat: MenuLink) => menuCat.component === "categories_multi_list"
			)[0]?.categories || [];

		const appData = {
			siteLogo: data.story.content.site_logo,
			siteLogoDimensions: {
				width: Number(
					data.story.content.site_logo.filename.split("/")[5].split("x")[0]
				),
				height: Number(
					data.story.content.site_logo.filename.split("/")[5].split("x")[1]
				)
			},
			search: data.story.content.search,
			account: data.story.content.account,
			topNavbarLeft: data.story.content.top_navbar_left,
			menuCategories: {
				left: data.story.content.menu_categories_left
					? filterMenuCategories(data.story.content.menu_categories_left)
					: [],
				right: data.story.content.menu_categories_right
					? filterMenuCategories(data.story.content.menu_categories_right)
					: []
			},
			menuCategoriesNorce: {
				left:
					norceMenuLeft.length > 0
						? getMenuCategoriesFromNorce({
								categoriesTree,
								norceMenuCategoriesFromStoryblok: norceMenuLeft
							})
						: [],
				right:
					norceMenuRight.length > 0
						? getMenuCategoriesFromNorce({
								categoriesTree,
								norceMenuCategoriesFromStoryblok: norceMenuRight
							})
						: []
			},
			applicationNorce: application,
			culturesNorce: application.Cultures.List.map((culture) => ({
				label: culture.Name.replace(/\(.*\)/g, "").trim(),
				value: culture.Code
			})).sort((a) => (a.value === application.Cultures.Default.Code ? -1 : 0)),
			currenciesNorce: norceStore.currenciesNorce,
			headerLoaded: true,
			materials: data.story.content.materials,
			gallery: data.story.content.images,
			flags: data.story.content.flags,
			topBar: data.story.content.top_bar,
			footerLogo: data.story.content.footer_logo
		};

		appStore.initState(appData);
		norceStore.initState(application);

		const paramInfo = await getParametricsInformation({
			...norceStore.storeContext
		});
		productStore.addParametricsInformation(paramInfo);
		productStore.addDefaultProductImage(
			data.story.content.default_image.filename
		);
		productStore.setPriceLists(priceLists);
		if (getUser?.value?.Email) {
			await cartStore.initCartUser();
		} else {
			await cartStore.initCart();
		}
	};

	return {
		initApp
	};
};
